import Axios from 'axios';
import Collection from '@/stores/abstract/Collection';
import Model from '@/stores/abstract/Model';
import axiosInstance from '@/helpers/axios';
import stores from '@/stores/index';
import {generateLog} from '@/helpers/functions';

const CancelToken: any = Axios.CancelToken;
let cancel: any;
const URL: string = '/users';

export class Users extends Model<IUser> implements IUser {

  id!: number | null;
  erpId!: string;
  name!: string;
  username!: string;
  email!: string;
  password!: string;
  passwordConfirmation!: string;
  dashboardId!: string|number;
  mobileDashboardId!: string|number;
  pin!: string|number;
  adult!: boolean;
  pinRequired!: boolean;
  locked!: boolean;
  active!: boolean;
  default!: boolean;
  accounts!: IAccount[];
  dashboard!: IDashboard | null;
  mobileDashboard!: IDashboard | null;
  customer!: ICustomer | null;
  pmsId!: number | null;

  constructor(attributes: any = {}, parentCollection?: any) {
    super(
      {
        ...{
          id: null,
          erpId: '',
          name: '',
          username: '',
          email: '',
          password: '',
          dashboardId: '',
          adult: false,
          pinRequired: false,
          accounts: [],
        }, ...attributes,
      }, // Default values
      parentCollection,
    );
  }

  urlRoot(): string {
    return URL;
  }
}

export default class UsersCollection extends Collection<Users> {

  routeName = 'users';

  model(): Constructor<Users> {
    return Users;
  }

  url(): string {
    return URL;
  }

  fetchData(index: number | null, search: string | null = '', perPage: number | 10, additional?: string | '') {
    if (cancel !== undefined) {
      cancel();
    }

    const params = new URLSearchParams({
      page: String(index ?? 1),
      perpage: String(perPage),
    });

    if (search) {
      params.append('search', search);
    }

    let url = `${this.url()}?${params.toString()}`;
    if (additional) {
      // ensure additional starts with & or ? properly
      url += additional.startsWith('&') ? additional : `&${additional}`;
    }

    if (search) {
      return axiosInstance.get(url, {
        cancelToken: new CancelToken(function executor(c: any) {
          cancel = c;
        }),
      })
        .then((response) => response.data.data).catch((e) => {
          const log: any = {
            route: this.routeName,
            message: e,
          };
          generateLog(log);
        });
    } else {
      return axiosInstance.get(url, {
        cancelToken: new CancelToken(function executor(c: any) {
          cancel = c;
        }),
      })
        .then((response) => response.data.data).catch((e) => {
          const log: any = {
            route: this.routeName,
            message: e,
          };
          generateLog(log);
        });
    }
  }

  createNewItem(newUser: IUser) {
    return axiosInstance.post(`${this.url()}`, newUser)
      .then((response) => {
        stores.Language.loadTranslations();
        return response.data;
      }).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  deleteItem(id: number) {
    return axiosInstance.delete(`${this.url()}/${id}`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  deleteItems(ids: any[]) {
    return axiosInstance.post(`${this.url()}/delete`, {ids})
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  attachAccounts(idList: number[], userId: number) {
    return axiosInstance.post(`${this.url()}/${userId}/accounts/attach`, {ids: idList})
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  attachDashboard(userId: any, dashboardId: any) {
    return axiosInstance.post(`${this.url()}/${userId}/dashboard/${dashboardId}/attach`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  attachMobileDashboard(userId: any, dashboardId: any) {
    return axiosInstance.post(`${this.url()}/${userId}/dashboard/${dashboardId}/attachMobile`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  detachDashboard(userId: any) {
    return axiosInstance.post(`${this.url()}/${userId}/dashboard/detach`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  detachMobileDashboard(userId: any) {
    return axiosInstance.post(`${this.url()}/${userId}/dashboard/mobileDetach`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  searchUserAccounts(userId: any, keyword: string) {
    return axiosInstance.post(`${this.url()}/${userId}/${keyword}/account`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }
  attachCustomer(idList: number[], userId: number) {
    return axiosInstance.post(`${this.url()}/${userId}/customers/attach`, {ids: [idList]})
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  detachCustomers(idList: number[], userId: number) {
    return axiosInstance.post(`${this.url()}/${userId}/customers/detach`, {ids: idList})
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }
  detachAccount(user: IUser, account: IAccount) {
    return axiosInstance.get(`${this.url()}/${user.id}/${account.id}/accounts/detach`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  updateItem(id: any, user: any) {
    return axiosInstance.put(`${this.url()}/${id}`, user)
      .then((response) => {
        // Log the entire response for debugging
        console.log('Update Item Response:', response);
        // Extract and log response data
        console.log('Response Data:', response.data);
        return response.data;
      })
      .catch((e) => {
        // Prepare and log the log object
        const log: any = {
          route: this.routeName,
          message: e.message,
          response: e.response ? e.response.data : 'No response data'
        };
        generateLog(log);
        // Return the status and message to the component
        return {
          responseCode: e.response ? e.response.status : 500,
          message: e.response ? e.response.data.error.message : e.message
        }
      });
  }


  getSelectedUser(id: number) {
    return axiosInstance.get(`${this.url()}/${id}`)
      .then((response) => response.data)
      .catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  searchUsers(input: string, page: number, source: any, perPage: number | 10) {
    return axiosInstance.get(`${this.url()}?query=${input}&page=${page}&perPage=${perPage}`, {cancelToken: source.token})
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  getUsersOnPage(index: number) {
    return axiosInstance.get(`${this.url()}?page=${index}`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  getAllUsers() {
    return axiosInstance.get(`${this.url()}`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  getUserValidationRules() {
    return axiosInstance.get(`user/validation`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  importUsers(data: any, tableName: string) {
    return axiosInstance.post(`import/${tableName}`, {data})
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }

  exportUsers(tableName: string) {
    return axiosInstance.get(`export/${tableName}`)
      .then((response) => response.data).catch((e) => {
        const log: any = {
          route: this.routeName,
          message: e,
        };
        generateLog(log);
      });
  }
}
